/**
 * O filtro de status do campo "Comunidade".
 *
 * Comunidade inativa não sai do sistema - as métricas dela continuam valendo -, então o campo precisa
 * separar as duas coisas: mostra só as ativas por padrão e guarda as inativas atrás do interruptor, em
 * vez de obrigar o produtor a desmarcar uma por uma cada vez que a lista cresce.
 */

type CommunityStatus = { id: number; active: boolean };

/**
 * A lista que o campo mostra. Com o interruptor ligado, só as ativas.
 */
export function filterCommunitiesByStatus<T extends CommunityStatus>(
  communities: T[],
  onlyActive: boolean,
): T[] {
  if (!onlyActive) return communities;

  return communities.filter((community) => community.active);
}

/**
 * A seleção com que o campo abre quando o navegador não lembra de nenhuma: as ativas.
 *
 * Quem não tem nenhuma ativa cai de volta em todas, e não em nenhuma - senão a tela abriria vazia,
 * avisando que é preciso selecionar ao menos uma comunidade, para uma conta que não tem outra opção.
 */
export function resolveDefaultCommunityIds<T extends CommunityStatus>(
  communities: T[],
): number[] {
  const activeIds = communities
    .filter((community) => community.active)
    .map((community) => community.id);

  return activeIds.length
    ? activeIds
    : communities.map((community) => community.id);
}

/**
 * A seleção depois de um clique na grid, sabendo que parte da lista está escondida.
 *
 * Só linha visível pode ser marcada ou desmarcada. Sem isso, um "desmarcar todas" durante uma busca
 * apagaria a seleção inteira - inclusive as comunidades que a busca deixou de fora e que o produtor
 * nunca viu naquela tela.
 *
 * Isto vale para o que está escondido AGORA. Mexer no interruptor é outra coisa: ali a comunidade sai da
 * lista, e quem sai da lista sai da seleção - a modal cuida disso.
 */
export function mergeVisibleSelection(
  currentIds: number[],
  incomingIds: number[],
  visibleIds: number[],
): number[] {
  const hidden = currentIds.filter((id) => !visibleIds.includes(id));
  const visibleSelection = incomingIds.filter((id) => visibleIds.includes(id));

  return [...new Set([...hidden, ...visibleSelection])];
}

/**
 * O contador do rodapé, que conta sobre o escopo do filtro e não sobre o resultado da busca - senão o
 * total dançaria a cada letra digitada.
 *
 * O que o filtro esconde e continua selecionado é dito em voz alta. Mexer no interruptor desmarca quem
 * sai da lista, então isto é sobre a seleção que veio guardada do navegador: quem já tinha inativas nela
 * leria "5 de 5 ativas selecionadas" numa tela que está medindo oito comunidades, e o contador seria a
 * única coisa na modal a não mencionar as outras três.
 */
export function formatCommunitiesSelectionLabel(
  selectedCount: number,
  totalCount: number,
  onlyActive: boolean,
  hiddenSelectedCount = 0,
): string {
  const scope = onlyActive ? 'ativas selecionadas' : 'selecionadas';
  const hidden = hiddenSelectedCount
    ? `, mais ${hiddenSelectedCount} ${
        hiddenSelectedCount === 1 ? 'inativa' : 'inativas'
      }`
    : '';

  return `${selectedCount} de ${totalCount} ${scope}${hidden}`;
}
